import React, { useState } from 'react'
import dayjs from 'dayjs';
import Sheet from '@mui/joy/Sheet';
import Switch from '@mui/material/Switch';
import IconButton from '@mui/joy/IconButton';
import Skeleton from 'react-loading-skeleton';
import CssBaseline from '@mui/joy/CssBaseline';
import Select, { SingleValue } from "react-select";
import InvoiceModal from './InvoiceModal/InvoiceModal';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import TextAreaCustom from '../../../../../../common/TextAreaCustom';
import useVendors from '../../../../../../store/ReactQueries/procurements/vendors/useVendors';
import usePurchaseOrderTypes from '../../../../../../store/ReactQueries/procurements/purchaseOrderTypes/usePurchaseOrderTypes';
import { ModeTypes } from '../Components/AddPurchaseOrder';
import { FieldErrors } from '../../../../../../domain/helpers/Errors';
import { useDarkModeStore } from '../../../../../../store/useDarkModeStore';
import { useLanguageStore } from '../../../../../../store/useLanguageStore';
import { darkModeBackgroudInputs } from '../../../../../../utils/constants';
import { PurchaseOrderInterface } from '../../../../../../models/ProcurementModule/PurchateOrder/PurchaseOrder';
import { getPurchaseOrderStatusInfo, PurchaseOrderStatus, purchaseOrderStatusArray } from '../../../../../../enums/purchaseOrderStatus';

interface BasicInformationProps {
    mode: ModeTypes;
    loading: boolean;
    isApproved: boolean;
    form: PurchaseOrderInterface;
    isRequestForQuotation: boolean;
    errors: FieldErrors | undefined;
    setForm: React.Dispatch<React.SetStateAction<PurchaseOrderInterface>>;
    setFileNames: React.Dispatch<React.SetStateAction<{
        fileUrl: string;
        fileName: string;
    }[]>>;
}

export const BasicInformation: React.FC<BasicInformationProps> = ({
    mode, form, isApproved, isRequestForQuotation, errors, loading,
    setForm, setFileNames,
}) => {

    const isView = mode === 'View';
    const isAdd = mode === 'Add';
    const { darkMode } = useDarkModeStore();
    const { language } = useLanguageStore();
    const { data: purchaseOrderTypes } = usePurchaseOrderTypes(0, 0);
    const { data: vendors } = useVendors(0, 0);

    const [userTitle, setUserTitle] = useState<string>(form?.purchaseOrderType ? form.purchaseOrderType.title : '');
    const [vendorTitle, setVendorTitle] = useState<string>(form?.vendor ? form.vendor.name : '');
    const [statuses, setStatuses] = useState<PurchaseOrderStatus>(form?.status ? form.status : PurchaseOrderStatus.Pending);
    const [invoiceModal, setInvoiceModal] = useState<boolean>(false);

    let items: { label: string, fieldName: string, value: string, readOnly: boolean, type: 'text' | 'number' | 'date' | 'select' | 'boolean' | 'multitext', display?: 'none' | 'initial' }[] = [
        { label: 'Code', fieldName: 'code', value: form?.code ?? '', readOnly: true, type: 'text', display: isAdd ? 'none' : 'initial' },
        { label: 'Date', fieldName: 'date', value: form.date ? dayjs(form.date).format('YYYY-MM-DD') : '', readOnly: true, type: 'date' },
        { label: 'PurchaseOrderType', fieldName: 'purchaseOrderTypeId', value: form?.purchaseOrderTypeId ?? '', readOnly: false, type: 'select' },
        {
            label: 'Status',
            fieldName: 'status',
            value: form?.status
                ? language[getPurchaseOrderStatusInfo(form.status as PurchaseOrderStatus).name] ?? getPurchaseOrderStatusInfo(form.status as PurchaseOrderStatus).name
                : '',
            readOnly: true,
            type: 'select'
        },
        { label: 'Vendor', fieldName: 'vendorId', value: form?.vendorId ?? '', readOnly: false, type: 'select' },
    ];

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>, fieldName: string) => {
        const { value, type } = e.target;
        delete errors?.[fieldName];
        const result = type === 'number' ? parseFloat(value) : value;
        setForm({ ...form, [fieldName]: result });
    }

    const handleSelect = (
        selectedOption: SingleValue<{
            label: any;
            value: string;
        }>,
        fieldName: string
    ) => {
        delete errors?.[fieldName];
        const isVendor = fieldName === 'vendorId';
        if (!selectedOption) {
            if (fieldName !== 'status') {
                setForm({
                    ...form,
                    [fieldName]: "",
                    [fieldName.replace("Id", "")]: { [isVendor ? 'name' : 'title']: '' },
                });
            } else {
                setForm({
                    ...form,
                    [fieldName]: PurchaseOrderStatus.Pending
                })
            }
            return;
        }

        const upForm = { ...form };

        if (fieldName === 'status') upForm[fieldName] = selectedOption.value as unknown as PurchaseOrderStatus;
        else {
            upForm[fieldName] = selectedOption.value;
            upForm[fieldName.replace("Id", "")] = { [isVendor ? 'name' : 'title']: selectedOption.label };
        }

        setForm(upForm);

    };

    const handleChangeBool = (e: React.ChangeEvent<HTMLInputElement>, fieldName: string) => {
        setForm((prevForm) => ({
            ...prevForm,
            [fieldName]: e.target.checked,
        }));
    };

    const handleInvoiceModal = () => setInvoiceModal(!invoiceModal);

    const hasRequestforQuotationInput = () => {
        if (!isView) return !form.hasInvoice;
        else return true;
    }

    return (
        <div className='w-100 d-flex justify-content-center'>
            <CssBaseline />
            <Sheet
                sx={{
                    width: '100%',
                    my: 0,
                    py: 4,
                    px: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0,
                    borderRadius: 'md',
                    position: 'relative',
                    boxShadow: '8px 8px 30px rgba(0, 0, 0, 0.2)',
                    background: darkMode ? 'var(--dark-mode-bg)' : 'white',
                }}
            >
                <h5 className='mb-0 ' style={{ color: darkMode ? 'white' : 'rgba(0, 0, 0, 0.6)' }}>
                    {language['BasicInformation']}
                </h5>
                <div className='basic-information-inputs mt-4'>
                    {
                        items.map(({ label, value, readOnly, type, fieldName, display = 'inital' }) =>
                            loading
                                ? <Skeleton
                                    key={`basic-info-loading-${label}`}
                                    className="mt-4"
                                    style={{ width: '100%', height: 41 }}
                                />
                                : <div
                                    className='form form-group'
                                    key={`basic-information-${label}`}
                                    style={{ display }}
                                >
                                    <label
                                        title={language[label] ?? label}
                                        id={label}
                                        htmlFor={label}
                                        className={`${darkMode ? 'text-white' : 'text-dark'}`}
                                    >
                                        {language[label] ?? label}
                                    </label>
                                    {
                                        type === 'select'
                                            ? <Select
                                                id={label}
                                                key={label}
                                                value={{
                                                    label:
                                                        fieldName === 'purchaseOrderTypeId'
                                                            ? `${form?.purchaseOrderType?.title ?? ""}`
                                                            : fieldName === 'vendorId'
                                                                ? `${form?.vendor?.name ?? ""}`
                                                                : fieldName === 'status'
                                                                    ? `${language[getPurchaseOrderStatusInfo(form?.status).name] ?? language[getPurchaseOrderStatusInfo(PurchaseOrderStatus.Pending).name]}`
                                                                    : null
                                                    ,
                                                    value: form?.[fieldName] || "",
                                                }}
                                                className={`${darkMode && "dark-mode-input"}`}
                                                onChange={(e) => handleSelect(e, fieldName)}
                                                onInputChange={(inputValue) =>
                                                    fieldName === 'vendorId'
                                                        ? setVendorTitle(inputValue)
                                                        : fieldName === 'purchaseOrderTypeId'
                                                            ? setUserTitle(inputValue)
                                                            : fieldName === 'status'
                                                                ? setStatuses(inputValue as unknown as PurchaseOrderStatus)
                                                                : null
                                                }
                                                placeholder="Zgjedh"
                                                options={
                                                    fieldName === 'purchaseOrderTypeId'
                                                        ? purchaseOrderTypes?.map(({ id, title }) => ({
                                                            value: id ?? "",
                                                            label: `${title ?? ""}`,
                                                        }))
                                                        : fieldName === 'vendorId'
                                                            ? vendors?.map(({ id, name }) => ({
                                                                value: id ?? "",
                                                                label: `${name ?? ""}`,
                                                            }))
                                                            : fieldName === 'status'
                                                                ? purchaseOrderStatusArray.map(({ value, label }) => ({ value, label: language[label] ?? label }))
                                                                : []
                                                }
                                                isClearable
                                                isDisabled={isView || isApproved || readOnly || isRequestForQuotation}
                                                styles={{
                                                    menu: (provided) => ({
                                                        ...provided,
                                                        zIndex: 11,
                                                    }),
                                                    container: (provided) => ({
                                                        ...provided,
                                                        position: "relative",
                                                        zIndex: 11,
                                                        color: darkMode ? 'white' : 'initial',
                                                    }),
                                                    singleValue: (provided) => ({
                                                        ...provided,
                                                        color: darkMode ? 'white' : 'black',
                                                    }),
                                                    menuPortal: (base) => ({ ...base, zIndex: 99999, }),
                                                    control: (provided) => ({
                                                        ...provided,
                                                        backgroundColor: darkMode ? darkModeBackgroudInputs : "#F3F6F9",
                                                        borderColor: darkMode ? 'white' : "#DEE2E6",
                                                        background: darkMode ? darkModeBackgroudInputs : 'white',
                                                    }),
                                                }}
                                                menuPlacement="bottom"
                                                menuPortalTarget={document.body}
                                            />
                                            : type === 'multitext'
                                                ? <TextAreaCustom
                                                    errors={{}}
                                                    form={form}
                                                    name={fieldName}
                                                    setForm={setForm}
                                                    view={isView || isApproved || isRequestForQuotation}
                                                />
                                                : <input
                                                    id={label}
                                                    value={value}
                                                    type={type}
                                                    onChange={(e) => handleChange(e, fieldName)}
                                                    title={value}
                                                    readOnly={isView || readOnly || isApproved || isRequestForQuotation}
                                                    className='form form-control text-truncate'
                                                    style={{
                                                        background: darkMode ? darkModeBackgroudInputs : 'initial',
                                                        color: darkMode ? 'white' : 'initial',
                                                    }}
                                                />
                                    }
                                    {
                                        errors?.[fieldName]
                                        && <div className='invalid-feedback-danger' style={{ display }}>
                                            {language[label] || label} {language['IsRequired']}
                                        </div>
                                    }
                                </div>
                        )}
                    <div
                        key={`fieldName-HasInvoice`}
                        className={` ${isView ? 'd-none' : 'd-flex'} flex-row align-items-center mt-3 gap-3 position-relative`}
                    >
                        <label
                            title={language['HasInvoice'] ?? 'HasInvoice'}
                            className={`cursor-pointer ${darkMode ? 'text-white' : 'text-dark'}`}
                        >
                            {language['HasInvoice'] ?? 'HasInvoice'}
                        </label>
                        <Switch
                            id={'HasInvoice'}
                            checked={Boolean(form['hasInvoice'])}
                            onChange={(e) => handleChangeBool(e, 'hasInvoice')}
                            sx={{
                                '& .MuiSwitch-thumb': {
                                    backgroundColor: 'var(--color-pink)',
                                },
                                '& .Mui-checked + .MuiSwitch-track': {
                                    backgroundColor: 'var(--color-pink)',
                                },
                                '& .Mui-disabled .MuiSwitch-thumb': {
                                    backgroundColor: 'var(--color-pink)',
                                },
                                '& .Mui-disabled + .MuiSwi  tch-track': {
                                    backgroundColor: 'lightgrey',
                                },
                            }}
                        />
                        {
                            Boolean(form['hasInvoice'])
                            && <div
                                className='d-flex flex-row align-items-center gap-1'
                                onClick={handleInvoiceModal}
                                role='button'
                            >
                                <IconButton
                                    aria-label="addinvoice"
                                    id="addinvoice"
                                    title={language['AddInvoice']}
                                    sx={{ borderRadius: '50%', color: 'var(--color-pink)' }}
                                >
                                    <AttachFileIcon width="20" height="20" />
                                </IconButton>
                                <span id='addinvoice'>{language['AttachInvoice'] ?? 'Attach the file here'}</span>
                            </div>


                        }
                        {
                            errors?.['hasInvoice']
                            && <div className='invalid-feedback-danger position-absolute text-center' style={{ bottom: -25 }}>
                                {errors?.['hasInvoice']}
                            </div>
                        }
                    </div>
                    {hasRequestforQuotationInput() && <div
                        key={`fieldName-requestForQuotation`}
                        className='d-flex flex-row align-items-center mt-3 gap-3 position-relative'
                    >
                        <label
                            title={language['RequestForQuotation'] ?? 'RequestForQuotation'}
                            className={`cursor-pointer ${darkMode ? 'text-white' : 'text-dark'}`}
                        >
                            {language['RequestForQuotation'] ?? 'RequestForQuotation'}
                        </label>
                        <Switch
                            id={'RequestForQuotation'}
                            checked={Boolean(form['requestForQuotation'])}
                            disabled={isView || isApproved || isRequestForQuotation}
                            onChange={(e) => handleChangeBool(e, 'requestForQuotation')}
                            sx={{
                                '& .MuiSwitch-thumb': {
                                    backgroundColor: 'var(--color-pink)',
                                },
                                '& .Mui-checked + .MuiSwitch-track': {
                                    backgroundColor: 'var(--color-pink)',
                                },
                                '& .Mui-disabled .MuiSwitch-thumb': {
                                    backgroundColor: 'var(--color-pink)',
                                },
                                '& .Mui-disabled + .MuiSwi  tch-track': {
                                    backgroundColor: 'lightgrey',
                                },
                            }}
                        />
                        {
                            isView && Boolean(form['hasInvoice'])
                            && <div className='d-flex flex-row gap-1 align-items-center justify-content-center'>
                                <span>{language['Invoices']}</span>
                                <IconButton
                                    aria-label="edit"
                                    title={language['AddInvoice']}
                                    sx={{ borderRadius: '50%', color: 'var(--color-pink)' }}
                                    onClick={handleInvoiceModal}
                                >
                                    <AttachFileIcon width="20" height="20" />
                                </IconButton>
                            </div>
                        }
                        {
                            isView && Boolean(form['hasInvoice']) && errors?.['hasInvoice']
                            && <div className='invalid-feedback-danger position-absolute text-center' style={{ bottom: -25 }}>
                                {errors?.['hasInvoice']}
                            </div>
                        }
                    </div>}
                </div>
            </Sheet>
            {
                invoiceModal
                && <InvoiceModal
                    mode={mode}
                    form={form}
                    errors={errors}
                    purchaseOrderId={form.id}
                    setForm={setForm}
                    setOpen={setInvoiceModal}
                    setFileNames={setFileNames}
                />
            }
        </div>
    );
}
export default BasicInformation